import { ScrollArea } from "@/components/core/scroller";
import { useAuth } from "@/hooks/use-auth";
import { IconArrowRight } from "@tabler/icons-react";
import clsx from "clsx";
import { useState } from "react";
import type { CaseRecord } from "../case-record/types/case-records";
import CaseEvidenceDefendantTab from "./components/case-evidence-defendant-tab";
import CaseEvidencePlaintiffTab from "./components/case-evidence-plaintiff-tab";
import type { Defendant } from "./types/defendant";

export default function CaseEvidence({
	data,
	depth = "0",
	readonly = false,
	members,
	index,
}: {
	data: CaseRecord;
	readonly: boolean;
	depth: string;
	members?: any;
	defendants?: Defendant[];
	index: number;
}) {
	const { user } = useAuth();
	const defendant = data.case_defendants.length === 0;
	const thirdPartyDefendant = members?.length === 0;
	const occ = user?.is_occ;
	//members is the new defendant for third party complaints

	const [activeIndex, setActiveIndex] = useState<number>(0);
	const [selectedDefendant, setSelectedDefendant] = useState(null);
	const [selected3rdPartyDefendant, setSelected3rdPartyDefendant] = useState(null);

	return (
		<div className="flex flex-row w-full">
			{!thirdPartyDefendant && depth === "0" ? (
				<div className="flex flex-row w-full max-h-[960px]">
					{/* evidence sidebar */}
					<ScrollArea className="flex flex-col h-full w-[300px] border-r border-border/50">
						<div className="flex flex-col w-full divide-y divide-border/50">
							<div className="flex flex-col w-full py-3">
								<div className="flex flex-row w-full px-3 py-1 ">
									<span className="font-semibold text-xs uppercase tracking-wide text-foreground/40">{data.plaintiffs.length} Plaintiff/s</span>
								</div>
								<div className="flex flex-col w-full">
									<button
										type="button"
										className={clsx(
											"group flex flex-row items-center justify-between w-full",
											"relative grayscale inline-flex items-center px-3 py-2.5 gap-1  font-semibold text-base text-foreground/70 hover:text-foreground-dark data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
											"before:content-[''] before:absolute before:opacity-0 before:pointer-events-none",
											"before:right-0 before:w-[2px] before:h-full",
											"data-[state=active]:grayscale-0 data-[state=active]:bg-primary-50 data-[state=active]:text-primary-600 data-[state=active]:before:opacity-100 data-[state=active]:before:bg-primary-600",
											activeIndex === 0 ? "grayscale-0 bg-primary-50 text-primary-600 before:opacity-100 before:bg-primary-600 hover:text-primary-600" : "",
										)}
										onClick={() => {
											setActiveIndex(0);
											setSelectedDefendant(null);
										}}
									>
										{data.plaintiffs.length === 1 ? (
											<span className="truncate font-semibold text-sm">{defendant?.full_name || data.plaintiffs[0]?.full_name}</span>
										) : (
											<span className="truncate font-semibold text-sm">{defendant?.full_name || "PLAINTIFFS"}</span>
										)}
										<IconArrowRight
											className={clsx("shrink-0 transition-colors", activeIndex === 0 ? "text-foreground" : "text-foreground/30 group-hover:text-foreground")}
											size={14}
											stroke={2}
										/>
									</button>
								</div>
							</div>
							<div className="flex flex-col w-full py-3">
								<div className="flex flex-row w-full px-3 py-1 ">
									<span className="font-semibold text-xs uppercase tracking-wide text-foreground/40">{data.case_defendants.length} Defendant/s</span>
								</div>
								<div className="flex flex-col w-full">
									{data.case_defendants?.map((def, index) => (
										<button
											key={def.id}
											type="button"
											className={clsx(
												"group flex flex-row items-center justify-between w-full",
												"relative grayscale inline-flex items-center px-3 py-2.5 gap-1  font-semibold text-base text-foreground/70 hover:text-foreground-dark data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
												"before:content-[''] before:absolute before:opacity-0 before:pointer-events-none",
												"before:right-0 before:w-[2px] before:h-full",
												"data-[state=active]:grayscale-0 data-[state=active]:bg-primary-50 !data-[state=active]:text-primary-600 data-[state=active]:before:opacity-100 data-[state=active]:before:bg-primary-600",
												activeIndex === index + 1
													? "grayscale-0 bg-primary-50 text-primary-600 before:opacity-100 before:bg-primary-600 hover:text-primary-600"
													: "",
											)}
											onClick={() => {
												setActiveIndex(index + 1);
												setSelectedDefendant(def);
											}}
										>
											<span className="truncate w-[200px] text-start font-semibold text-sm">{def?.full_name}</span>
											<IconArrowRight
												className={clsx(
													"shrink-0 transition-colors",
													activeIndex === index + 1 ? "text-foreground" : "text-foreground/30 group-hover:text-foreground",
												)}
												size={14}
												stroke={2}
											/>
										</button>
									))}
								</div>
							</div>
						</div>
					</ScrollArea>
					{/* evidence body */}
					<div className="flex flex-col w-full h-full">
						{activeIndex === 0 && <CaseEvidencePlaintiffTab caseData={data} readonly={readonly} depth={depth} index={0} />}

						{selectedDefendant !== null && (
							<CaseEvidenceDefendantTab
								defendant={selectedDefendant}
								defendants={data.case_defendants}
								caseData={data}
								readonly={readonly}
								depth={depth}
								index={0}
							/>
						)}
					</div>
				</div>
			) : (
				<>
					{!defendant &&
						!occ &&
						members.map((group: Defendant) => (
							<div key={group.id} className="flex flex-row w-full h-[calc(100svh-420px)]">
								<ScrollArea className="flex flex-col h-full w-[300px] border-r border-border/50">
									<div className="flex flex-col w-full divide-y divide-border/50">
										<div className="flex flex-col w-full py-3">
											<div className="flex flex-row w-full px-3 py-1 ">
												<span className="font-semibold text-xs uppercase tracking-wide text-foreground/40">{group.plaintiff.length} Plaintiff/s</span>
											</div>
											<div className="flex flex-col w-full">
												<button
													type="button"
													className={clsx(
														"group flex flex-row items-center justify-between w-full",
														"relative grayscale inline-flex items-center px-3 py-2.5 gap-1  font-semibold text-base text-foreground/70 hover:text-foreground-dark data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
														"before:content-[''] before:absolute before:opacity-0 before:pointer-events-none",
														"before:right-0 before:w-[2px] before:h-full",
														"data-[state=active]:grayscale-0 data-[state=active]:bg-primary-50 data-[state=active]:text-primary-600 data-[state=active]:before:opacity-100 data-[state=active]:before:bg-primary-600",
														activeIndex === 0
															? "grayscale-0 bg-primary-50 text-primary-600 before:opacity-100 before:bg-primary-600 hover:text-primary-600"
															: "",
													)}
													onClick={() => {
														setActiveIndex(0);
														setSelected3rdPartyDefendant(null);
													}}
												>
													<span className="truncate font-semibold text-sm">{group.plaintiff[0]?.full_name}</span>
													<IconArrowRight
														className={clsx(
															"shrink-0 transition-colors",
															activeIndex === 0 ? "text-foreground" : "text-foreground/30 group-hover:text-foreground",
														)}
														size={14}
														stroke={2}
													/>
												</button>
											</div>
										</div>
										<div className="flex flex-col w-full py-3">
											<div className="flex flex-row w-full px-3 py-1 ">
												<span className="font-semibold text-xs uppercase tracking-wide text-foreground/40">
													{group.plaintiff[0].defendants.length} Defendant/s
												</span>
											</div>

											<div className="flex flex-col w-full">
												{group.plaintiff[0].defendants?.map((def, index) => (
													<button
														key={def.id}
														type="button"
														className={clsx(
															"group flex flex-row items-center justify-between w-full",
															"relative grayscale inline-flex items-center px-3 py-2.5 gap-1  font-semibold text-base text-foreground/70 hover:text-foreground-dark data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
															"before:content-[''] before:absolute before:opacity-0 before:pointer-events-none",
															"before:right-0 before:w-[2px] before:h-full",
															"data-[state=active]:grayscale-0 data-[state=active]:bg-primary-50 !data-[state=active]:text-primary-600 data-[state=active]:before:opacity-100 data-[state=active]:before:bg-primary-600",
															activeIndex === index + 1
																? "grayscale-0 bg-primary-50 text-primary-600 before:opacity-100 before:bg-primary-600 hover:text-primary-600"
																: "",
														)}
														onClick={() => {
															setActiveIndex(index + 1);
															setSelected3rdPartyDefendant(def);
														}}
													>
														<span className="truncate font-semibold text-sm">{def?.full_name}</span>
														<IconArrowRight
															className={clsx(
																"shrink-0 transition-colors",
																activeIndex === index + 1 ? "text-foreground" : "text-foreground/30 group-hover:text-foreground",
															)}
															size={14}
															stroke={2}
														/>
													</button>
												))}
											</div>
										</div>
									</div>
								</ScrollArea>

								<div className="flex flex-col w-full h-full">
									{activeIndex === 0 && <CaseEvidencePlaintiffTab caseData={data} readonly={readonly} depth={depth} defendant={group.plaintiff[0]} index={0} />}

									{selected3rdPartyDefendant !== null && (
										<CaseEvidenceDefendantTab
											// defendant={data.case_defendants[0]}
											defendant={selected3rdPartyDefendant}
											defendants={group.plaintiff[0].defendants}
											caseData={data}
											readonly={readonly}
											depth={depth}
											index={0}
										/>
									)}
								</div>
							</div>
						))}
				</>
			)}
		</div>
	);
}
